import {
  Button,
  ButtonGroup,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Select,
  Textarea,
} from '@/components'
import { useAxios } from '@/context/axiosContext'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Col } from 'styled-bootstrap-grid'
import { useDialog } from '@/context/dialogContext'
import { useAppSelector } from '@/redux/hooks'
import styled from 'styled-components'
import { formatDate } from '@/utils/date'
import commonUtils from '@/utils/commonUtils'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export const TextareaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

export const AuthorInfo = styled.div`
  width: 100%;
  font-size: 14px;
  color: #555;
  margin-bottom: 4px;
`

export const TextareaWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`

export const StyledTextarea = styled.textarea`
  flex: 1;
  padding: 25px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: none;

  &:disabled {
    color: #999;
    cursor: not-allowed;
  }
`

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 8px;
`

export const DtlButton = styled.button<{
  color?: string
  disabled?: boolean
  content?: string
  display?: string
}>`
  padding: 8px;
  margin-bottom: 4px;
  background-color: ${({ color }) => color || '#007bff'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  position: relative;
  display: ${({ display }) =>
    display || 'block'}; /* 버튼의 display 속성 설정 */

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.6 : 0.8)};
  }

  &::after {
    content: '${({ content }) => content || ''}';
    display: block;
    font-size: 12px;
    margin-top: 4px;
    color: white;
  }
`

interface MessageDetailItem {
  isNew: true | false
  useYn: 'Y' | 'N'
  regId: string
  regDate: string
  updId: string
  updDate: string
  msgCd: string
  msgExp: string
  typeDivCd: string
}

const DetailMessageMgmt: React.FC = () => {
  const axios = useAxios()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAppSelector((state) => state.auth)
  const { showAlert } = useDialog()
  location.state
  const [requestData] = useState({ msgCd: location.state?.msgCd || '' })

  const [message, setMessage] = useState<MessageDetailItem>({
    isNew: !!requestData.msgCd ? false : true,
    useYn: 'Y',
    regId: !!requestData.msgCd ? '' : user.userId,
    regDate: '',
    updId: '',
    updDate: '',
    msgCd: '',
    msgExp: '',
    typeDivCd: '',
  })

  const [isVisible, setIsVisible] = useState<boolean>(false)
  const getMessageDetail = async () => {
    try {
      const response = await axios.get('/messageMgmt/detail', {
        params: { ...requestData },
      })
      const common = response.data.common
      if (common.resultCode == 'NOTICES0202') {
        const data = response.data.data
        setMessage(data.message)
      } else {
        const closeFunc = () => {
          navigate('/messageMgmt')
        }
        showAlert({
          type: 'error',
          message: common.resultMsg,
          title: common.resultCode,
          onClose: closeFunc,
        })
      }
    } catch (error) {
      console.error('Error fetching terms info:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<TInput>) => {
    const name = e.target.name
    let value = e.target.value
    if (name === 'messageFromDate' || name === 'messageToDate') {
      value = value.replace(/\D/g, '')
    }
    setMessage((prevState) => {
      return { ...prevState, [name]: value, ['updId']: user.userId }
    })
  }

  const updateMessage = async () => {
    try {
      if (!validationCheck()) {
        return
      }
      const sendData = { ...message }
      if (message.isNew) sendData['regId'] = user.userId
      const response = await axios.post(
        message.isNew
          ? '/messageMgmt/insertMessage'
          : '/messageMgmt/messageUpdate',
        message,
      )
      const common = response.data.common
      if (common.resultCode == 'NOTICEI0200')
        showAlert({
          type: 'success',
          message: '정상적으로 생성되었습니다.',
          title: '성공',
          onClose() {
            navigate('/messageMgmt')
          },
        })
      else if (common.resultCode == 'NOTICEU0200')
        showAlert({
          type: 'success',
          message: '정상적으로 수정되었습니다.',
          title: '성공',
        })
      else
        showAlert({
          type: 'error',
          message: common.resultMsg || '메시지 업데이트 실패',
          title: common.resultCode,
        })
    } catch (error) {
      console.error('Error fetching terms reg:', error)
    }
  }

  const validateDateFormat = (str: string): boolean => {
    const datePattern = /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/
    return datePattern.test(str)
  }

  // 메시지 코드 유효성 검사 (AAA12345 형식 체크)
  const validateMsgCd = (value: any) => /^[A-Z]{3}\d{5}$/.test(value)

  const validationCheck = (): boolean => {
    let retVal = true
    let msg = ''
    if (message.msgCd.trim().length == 0 || message.msgCd.trim().length > 8) {
      retVal = false
      msg = '메시지 코드를 확인해주세요.'
    } else if (message.msgExp.trim().length == 0) {
      retVal = false
      msg = '메시지 내용을 확인해주세요.'
    }

    const upperCaseValue = message.msgCd.toUpperCase()

    message.msgCd = upperCaseValue

    if (!validateMsgCd(upperCaseValue)) {
      retVal = false
      msg = '업무코드 3자리(대문자) + 일련번호 5자리(숫자)로 입력해주세요.'
    }

    if (msg != '') showAlert({ type: 'warning', message: msg })
    return retVal
  }

  const toggleVisibility = () => {
    setIsVisible((prevState) => !prevState)
  }

  useEffect(() => {
    if (!message.isNew) {
      getMessageDetail()
    }
  }, [])

  const [isMsgCdDisabled, setIsMsgCdDisabled] = useState<boolean>(
    !!requestData.msgCd,
  )

  useEffect(() => {
    setIsMsgCdDisabled(!!requestData.msgCd) // 초기 로딩 시 msgCd 값이 있으면 비활성화
  }, [requestData.msgCd])

  return (
    <>
      <div style={{ width: '100%', height: '100%', marginTop: '50px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
            marginTop: '50px',
          }}
        >
          <p style={{ fontSize: '20px' }}>메시지 관리 등록/수정</p>
          <div style={{ display: 'flex', gap: '5px' }}></div>
        </div>
      </div>
      <div>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="msgCd">
                메시지 코드
                <span style={{ color: 'red', fontSize: '12px' }}>
                  (코드 규칙 : 업무코드 3자리 + 일련번호 5자리)
                </span>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="msgCd"
                name="msgCd"
                type="text"
                maxLength={8}
                value={message.msgCd}
                onChange={handleInputChange}
                disabled={isMsgCdDisabled}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="msgExp">
                메시지 내용
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Textarea
                id="msgExp"
                name="msgExp"
                value={message.msgExp}
                height={'100px'}
                onChange={handleInputChange}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="useYn">
                사용여부
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="useYn"
                name="useYn"
                value={message.useYn}
                onChange={handleInputChange}
              >
                <option value={'Y'}>사용</option>
                <option value={'N'}>미사용</option>
              </Select>
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="typeDivCd">
                메시지구분
                <span style={{ color: 'red', fontSize: '12px' }}>
                  (* 메시지에 대한 유형 분류입니다. 에러시 노출되는건지, 기본
                  메시지인지..등)
                </span>
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Select
                id="typeDivCd"
                name="typeDivCd"
                value={message.typeDivCd}
                onChange={handleInputChange}
              >
                <option value={''}>메시지구분</option>
                <option value={'0'}>에러</option>
                <option value={'1'}>기본</option>
                <option value={'2'}>알림</option>
              </Select>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="regDate">
                등록 일시
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="regDate"
                name="regDate"
                type="text"
                placeholder=""
                maxLength={3}
                value={commonUtils.formatDate(message.regDate, true)}
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col col>
            <FormGroup mb="20px">
              <Label display="block" mb="5px" htmlFor="updDate">
                수정 일시
              </Label>
              <div style={{ marginBottom: '5px' }} />
              <Input
                id="updDate"
                name="updDate"
                type="text"
                placeholder=""
                maxLength={100}
                value={commonUtils.formatDate(message.updDate, true)}
                disabled={true}
              />
            </FormGroup>
          </Col>
        </Row>
        <div
          style={{
            display: 'flex',
            justifyContent: 'right',
            width: '100%',
            marginBottom: '10px',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', gap: '5px' }}>
            <Button
              onClick={() => {
                navigate('/messageMgmt')
              }}
              color="neverbe"
            >
              목록
            </Button>
            <Button
              onClick={() => {
                updateMessage()
              }}
              color="neverbe"
            >
              저장
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
export default DetailMessageMgmt
